import { zodResolver } from '@hookform/resolvers/zod';
import {
  InlineAlertType,
  ProfileChangeResultTypenames,
  ProfileChangeTypeInput,
} from '@varo/graphql-schema-types';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import useProfileChange from 'features/myVaro/hooks/useProfileChange';
import { OtpFlowKey } from 'features/otp/constants/OtpFlowKey';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import Button from 'shared/components/vxl/Button/Button';
import InlineAlert from 'shared/components/vxl/InlineAlert/InlineAlert';
import VXLTextInput from 'shared/components/vxl/input/VXLTextInput/VXLTextInput';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import KeyboardAvoidingContainer from 'shared/containers/KeyboardAvoidingContainer/KeyboardAvoidingContainer';
import theme from 'shared/styles/theme';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';
import {
  clearPhoneNumberFormatting,
  formatPhoneNumber,
  getLocalPhoneDigits,
  hasInvalidPhoneAreaCode,
  PHONE_DIGIT_COUNT,
  PHONE_MAX_LENGTH,
} from 'shared/utils/string/phone';
import { z } from 'zod';

const phoneValidationMessages = {
  invalidAreaCode: 'Enter a valid area code',
  invalidLength: 'Number must be 10 digits',
  required: 'Required',
};

const changePhoneSchema = z
  .string({ error: phoneValidationMessages.required })
  .min(1, { error: phoneValidationMessages.required })
  .refine((value) => !hasInvalidPhoneAreaCode(value), {
    error: phoneValidationMessages.invalidAreaCode,
  })
  .refine((value) => getLocalPhoneDigits(value).length === PHONE_DIGIT_COUNT, {
    error: phoneValidationMessages.invalidLength,
  });

const changePhoneNumberSchema = z.object({
  phone: changePhoneSchema,
});

const ChangePhoneNumberScreen = () => {
  const router = useRouter();
  const [phoneNumber, setPhoneNumber] = useState<string>('');

  const [errorContent, setErrorContent] = useState<{
    name: string;
    message: string;
  } | null>(null);
  const { fetching, error, profileChange } = useProfileChange();
  const { t: translatedText } = useTranslation('myVaro', {
    keyPrefix: 'changePhone',
  });
  const {
    control,
    formState: { errors },
  } = useForm({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: {
      phone: phoneNumber,
    },
    resolver: zodResolver(changePhoneNumberSchema),
  });

  useEffect(() => {
    if (error && !errorContent) {
      setErrorContent({
        name: translatedText('error.header'),
        message: translatedText('error.description'),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const confirmChangePhoneNumber = async (newPhone: string) => {
    try {
      const response = await profileChange(
        {
          phoneNumberChangeRequest: {
            phoneNumber: clearPhoneNumberFormatting(newPhone),
          },
        },
        ProfileChangeTypeInput.PhoneNumberChange,
      );
      const results = response?.data?.profileChange;
      if (
        results?.__typename ===
          ProfileChangeResultTypenames.ProfileChangeSuccess &&
        newPhone
      ) {
        router.push({
          pathname: '/myvaro/personal-details/change-phone/enter-code',
          params: {
            phoneNumber: newPhone,
            challengeId: results.challengeId ?? '',
            flowKey: OtpFlowKey.PROFILE_CHANGE,
          },
        });
      } else if (
        results?.__typename === ProfileChangeResultTypenames.ProfileChangeError
      ) {
        setErrorContent({
          name: results.message,
          message: results.title,
        });
        logErrorWithContext(results?.message, {
          featureArea: FeatureArea.CHANGE_PHONE_NUMBER,
          responsibleTeam: ResponsibleTeam.FRAUD,
          severity: ErrorSeverity.LOW,
        });
      }
    } catch (error) {
      logErrorWithContext(error, {
        featureArea: FeatureArea.CHANGE_PHONE_NUMBER,
        responsibleTeam: ResponsibleTeam.FRAUD,
        severity: ErrorSeverity.LOW,
      });
    }
  };

  return (
    <KeyboardAvoidingContainer>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          title: translatedText('title'),
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              options={{
                headerBackVisible: true,
                title: translatedText('title'),
              }}
            />
          ),
        }}
      />
      <KeyboardAvoidingContainer.Content>
        {errorContent ? (
          <InlineAlert
            alertType={InlineAlertType.Error}
            isImageVisible={true}
            bodyText={errorContent?.name}
            onDismiss={() => {
              setErrorContent(null);
            }}
          />
        ) : null}
        <BodyBaseText numberOfLines={0} style={styles.phoneNumberLabel}>
          {translatedText('labels.editDescription')}
        </BodyBaseText>
        <Controller
          control={control}
          name="phone"
          render={({ field: { onChange, onBlur } }) => {
            const handleChange = (text: string) => {
              const formattedPhoneNumber = formatPhoneNumber(text);
              setPhoneNumber(formattedPhoneNumber);
              onChange(formattedPhoneNumber);
            };

            const handleBlur = () => {
              const formatted = formatPhoneNumber(phoneNumber);
              setPhoneNumber(formatted);
              onBlur();
            };
            return (
              <View>
                <VXLTextInput
                  value={phoneNumber}
                  onChangeText={handleChange}
                  onBlur={handleBlur}
                  autoFocus={true}
                  onSubmitEditing={async () => {
                    if (!errors.phone) {
                      await confirmChangePhoneNumber(phoneNumber);
                    }
                  }}
                  errorText={errors.phone?.message ?? ''}
                  labelText={translatedText('labels.inputLabel')}
                  autoCapitalize="none"
                  keyboardType="phone-pad"
                  maxLength={PHONE_MAX_LENGTH}
                  testID="text-input-phone"
                />
              </View>
            );
          }}
        />
      </KeyboardAvoidingContainer.Content>

      <KeyboardAvoidingContainer.Actions>
        <Button
          onPress={() => confirmChangePhoneNumber(phoneNumber)}
          isDisabled={!phoneNumber || !!errors.phone}
          label={translatedText('button.next')}
          isLoading={fetching}
          analyticsId="phone-number-change-submitted"
        />
      </KeyboardAvoidingContainer.Actions>
    </KeyboardAvoidingContainer>
  );
};

const styles = StyleSheet.create({
  phoneNumberLabel: {
    marginVertical: theme.spacing.m,
  },
});

export default ChangePhoneNumberScreen;
